import { Inject, inject, Injectable, makeStateKey, PLATFORM_ID, StateKey, TransferState } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpParams,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Constants } from '@helper/constants';
import { AuthStateService } from '@service/auth-state.service';
import { WindowDetectService } from '@service/window-detect.service';
import { Links } from '@helper/links';
import { DOCUMENT, isPlatformBrowser, isPlatformServer, Location } from '@angular/common';
import { LativPrefetcher } from '@service/lativ-prefetcher';
import { LativUrlEncoder } from '@service/lativ-url-encoder';
import { PreferenceKey } from '@helper/preference-key';
import { RouterHistoryService } from '@service/router-history.service';

import { DeviceDetectorService } from 'ngx-device-detector';
import { catchError, delay, Observable, of, tap, throwError, timeout } from 'rxjs';

interface TransferHttpResponse {
  body: any;
  headers: Record<string, string[]>;
  status?: number;
  statusText?: string;
  url?: string;
  responseType?: HttpRequest<unknown>['responseType'];
}

@Injectable({
  providedIn: 'root',
})
export class LativAPIInterceptor implements HttpInterceptor {
  static readonly lativNoToken = 'lativNoToken';
  static readonly lativTrueValue = 't';
  static readonly lativHttpTag = 'lativHttpTag';
  static readonly lativHttpValue = 'lativ';
  static readonly lativHttpConfig = { [LativAPIInterceptor.lativHttpTag]: LativAPIInterceptor.lativHttpValue };

  private readonly timeout = 25 * 1000;
  private readonly cacheDelay = 50;
  private readonly ssrPageStateKey = 'ssrPage';

  constructor(
    private device: DeviceDetectorService,
    private authService: AuthStateService,
    @Inject(PLATFORM_ID) private platformId: object,
    private windowDetectService: WindowDetectService,
    private routerHistoryService: RouterHistoryService,
    @Inject(DOCUMENT) private document: Document,
    private location: Location
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (
      req.url.includes(environment.fileScheme) ||
      req.url.includes(environment.legacyApiURL) ||
      !req.params.has(LativAPIInterceptor.lativHttpTag) ||
      req.params.get(LativAPIInterceptor.lativHttpTag) !== LativAPIInterceptor.lativHttpValue
    ) {
      // 如果 URL 是 file、legacy、或者 params 不是正確的 lativHttpTag, 就跳過.
      return next.handle(req);
    }
    const isNoToken =
      req.headers.has(LativAPIInterceptor.lativNoToken) && !!req.headers.get(LativAPIInterceptor.lativNoToken);
    if (isNoToken) {
      req = req.clone({
        headers: req.headers.delete(LativAPIInterceptor.lativNoToken),
      });
    }
    const ssrPageKey: StateKey<string> = makeStateKey(this.ssrPageStateKey);
    const transferState = inject(TransferState);
    const ssrKey = this.makeSsrKey(req.url);
    if (isPlatformServer(this.platformId)) {
      transferState.set<string>(ssrPageKey, ssrKey);
    }
    const storeKey = this.makeCacheKey(req);
    const response = transferState.get(storeKey, null);
    if (response) {
      const resp = of(
        new HttpResponse({
          body: response.body,
          headers: new HttpHeaders(response.headers),
          status: response.status,
          statusText: response.statusText,
          url: response.url,
        })
      );
      if (LativPrefetcher.shouldCache(response.url)) {
        if (transferState.get(ssrPageKey, '') === ssrKey) {
          return resp.pipe(
            tap(() => {
              if (!req.headers.get(LativPrefetcher.prefetchHeader)) {
                // prefetch should not affect the ssr api cache
                transferState.remove(ssrPageKey);
              }
            })
          );
        }
        // 若目前頁面是 SSR 頁面,
        // 那麼拿 transferState 資料就不用 delay,
        // delay 是為了 client 端, scroll restore,
        // 而 SSR 的話, 就不需要 restore scroll.
        const history = this.routerHistoryService.getHistory();
        if (history.length && history[history.length - 1].scrollTop > 0) {
          // 只有需要restore scroll的情況才delay
          return resp.pipe(delay(this.cacheDelay));
        }
        return resp;
      }
      return resp.pipe(tap(() => transferState.remove(storeKey)));
    }

    // set header.
    const params = new HttpParams({ encoder: new LativUrlEncoder(), fromString: req.params.toString() });
    const endPoint = isPlatformServer(this.platformId) ? environment.ssrEndPointUrl : Links.endPointUrl(this.document);
    let trackingId = '';
    const now = Date.now();
    if (isPlatformBrowser(this.platformId)) {
      try {
        const time = localStorage?.getItem(PreferenceKey.AD_TRACKING_TIME);
        const id = localStorage?.getItem(PreferenceKey.AD_TRACKING_ID);
        if (time && id) {
          // 86400000 one day
          if (now - parseInt(time) <= 86400000) {
            trackingId = id.substring(0, Constants.AD_TRACKING_ID_LENGTH);
          }
        }
      } catch {
        // ignore
      }
    }
    req = req.clone({
      params: params.delete(LativAPIInterceptor.lativHttpTag),
      url: `${endPoint}/${req.url}`,
      headers: req.headers
        .set('Content-Type', 'application/json')
        .set('Grpc-Metadata-lativ-store-api-version', Constants.API_VERSION)
        .set('Grpc-Metadata-lativ-store-app-platform', this.windowDetectService.isMobileDevice() ? '2' : '1')
        .set('Grpc-Metadata-lativ-store-user-agent', this.device.userAgent)
        .set('Grpc-Metadata-lativ-store-user-os', this.device.os)
        .set('Grpc-Metadata-lativ-store-client-version', Constants.CLIENT_VERSION)
        .set('Grpc-Metadata-lativ-store-locale', Links.getSiteLocale() ?? '')
        .set('Grpc-Metadata-lativ-store-user-timestamp', now.toString())
        .set('Grpc-Metadata-lativ-store-ad-tracking-id', trackingId),
    });
    // set accessToken.
    const token = this.authService.getToken();
    if (token && token.accessToken && !isNoToken) {
      req = req.clone({
        headers: req.headers.set('Authorization', [token.tokenType, token.accessToken].join(' ')),
      });
    }
    return next.handle(req).pipe(
      timeout(this.timeout),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          this.authService.logout();
        }
        return throwError(() => error);
      }),
      tap((event: HttpEvent<unknown>) => {
        if (
          event instanceof HttpResponse &&
          (isPlatformServer(this.platformId) || LativPrefetcher.shouldCache(event.url))
        ) {
          transferState.set<TransferHttpResponse>(storeKey, {
            body: event.body,
            headers: this.getHeadersMap(event.headers),
            status: event.status,
            statusText: event.statusText,
            url: event.url || '',
            responseType: req.responseType,
          });
        }
      })
    );
  }

  private makeCacheKey(request: HttpRequest<any>): StateKey<TransferHttpResponse> {
    const { params, method, responseType, url } = request;
    const encodedParams = params
      .keys()
      .sort()
      .map(k => `${k}=${params.getAll(k)}`)
      .join('&');
    const key = `${method}.${responseType}.${url}?${encodedParams}`;
    return makeStateKey(this.generateHash(key));
  }

  private makeSsrKey(url: string): string {
    return `${this.location.path()}-${url}`;
  }

  private getHeadersMap(headers: HttpHeaders): Record<string, string[]> {
    const headersMap: Record<string, string[]> = {};
    for (const key of headers.keys()) {
      const values = headers.getAll(key);
      if (values !== null) {
        headersMap[key] = values;
      }
    }
    return headersMap;
  }

  private generateHash(value: string): string {
    let hash = 0;
    for (const char of value) {
      hash = (Math.imul(31, hash) + char.charCodeAt(0)) << 0;
    }
    hash += 2147483647 + 1;
    return hash.toString();
  }
}

@Injectable({
  providedIn: 'root',
})
export class LativHttpClient {
  constructor(private http: HttpClient) {}

  get(
    lastPath: string,
    params?: { params: { [param: string]: string } },
    headers?: HttpHeaders | { [header: string]: string | string[] }
  ): Observable<object> {
    return this.http.get(lastPath, {
      headers,
      params: Object.assign(params?.params ?? {}, LativAPIInterceptor.lativHttpConfig),
    });
  }

  post(
    lastPath: string,
    body: any,
    params?: { params: { [param: string]: string } },
    headers?: any
  ): Observable<object> {
    return this.http.post(lastPath, body, {
      headers,
      params: Object.assign(params?.params ?? {}, LativAPIInterceptor.lativHttpConfig),
    });
  }

  put(body: any, lastPath: string, params?: { params: { [param: string]: string } }): Observable<object> {
    return this.http.put(lastPath, body, {
      params: Object.assign(params?.params ?? {}, LativAPIInterceptor.lativHttpConfig),
    });
  }

  patch(body: any, lastPath: string, params?: { params: { [param: string]: string } }): Observable<object> {
    return this.http.patch(lastPath, body, {
      params: Object.assign(params?.params ?? {}, LativAPIInterceptor.lativHttpConfig),
    });
  }

  delete(lastPath: string, params?: { params: { [param: string]: string } }): Observable<object> {
    return this.http.delete(lastPath, {
      params: Object.assign(params?.params ?? {}, LativAPIInterceptor.lativHttpConfig),
    });
  }
}
